import React, { useState } from 'react';
import { CalendarIcon, ClockIcon, HandIcon, MapPinIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { InitialAvatar } from '../../components/InitialAvatar';
import { EVENTS, PAST_EVENTS } from '../../data/events';
import type { PortalEvent, RsvpStatus } from '../../data/events';
import { CURRENT_MEMBER, MEMBERS, overlapWith, publicName } from '../../data/community';
import type { Member } from '../../data/community';
interface EventsProps {
  onOpenProfile: (member: Member) => void;
}
const CHOICES: {
  value: Exclude<RsvpStatus, null>;
  label: string;
}[] = [{
  value: 'going',
  label: 'Going'
}, {
  value: 'maybe',
  label: 'Maybe'
}, {
  value: 'cant',
  label: 'Can’t make it'
}];

/** Match a public name ("Theresa M.") back to a member record. */
function memberFor(name: string): Member | undefined {
  return MEMBERS.find(member => publicName(member.name) === name);
}
function AttendeeRow({
  name,
  onOpenProfile
}: {
  name: string;
  onOpenProfile: (member: Member) => void;
}) {
  const member = memberFor(name);
  const isYou = member?.name === CURRENT_MEMBER.fullName;
  const overlap = member && !isYou ? overlapWith(member, CURRENT_MEMBER) : null;
  const connected = member?.wavedBack;
  return <li className="flex items-center gap-3 py-2.5" data-id="element-618" data-mp="ar7v0rfyy9w1">
      <InitialAvatar name={member?.name ?? name} size={36} data-id="element-619" data-mp="arzdr1ogwd4w" />
      <span className="flex min-w-0 flex-1 flex-col" data-id="element-620" data-mp="et2sfkus434j">
        <span className="truncate text-small font-medium text-ink" data-id="element-621" data-mp="esjcg9oovot5">{isYou ? 'You' : name}</span>
        <span className="truncate text-small text-ink-muted" data-id="element-622" data-mp="jqbbdnnr4b6u">
          {overlap ? `Here when you were, ${overlap.start}–${overlap.end}` : member ? member.years : 'Member'}
        </span>
      </span>
      {member && !isYou ? connected ? <Button variant="quiet" onClick={() => onOpenProfile(member)} data-id="element-623" data-mp="yin0uu6yf9c1">
            See profile
          </Button> : <Button variant="secondary" onClick={() => onOpenProfile(member)} data-id="element-624" data-mp="2zj1fditzdp6q">
            <HandIcon aria-hidden="true" className="h-4 w-4" data-id="element-625" data-mp="2zgxe78ngv4a5" />
            Wave
          </Button> : null}
    </li>;
}
function EventCard({
  event,
  onOpenProfile
}: {
  event: PortalEvent;
  onOpenProfile: (member: Member) => void;
}) {
  const [rsvp, setRsvp] = useState<RsvpStatus>(null);
  const [showList, setShowList] = useState(false);
  const going = rsvp === 'going' ? [CURRENT_MEMBER.publicName, ...event.going] : event.going;
  const fromYourYears = going.filter(name => {
    const member = memberFor(name);
    return member && member.name !== CURRENT_MEMBER.fullName && overlapWith(member, CURRENT_MEMBER);
  }).length;
  return <article className="flex flex-col gap-4 rounded-card border border-rule bg-card p-5 shadow-card sm:p-6" data-id="element-626" data-mp="3n9nn1902p7g7">
      <div className="flex flex-col gap-2" data-id="element-627" data-mp="1wwy7yd0pubge">
        {event.era ? <span className="font-mono text-eyebrow-caption uppercase tracking-[0.14em] text-primary-text" data-id="element-628" data-mp="1sv38le2ci859">
            For the {event.era}
          </span> : null}
        <h2 className="text-heading font-semibold text-ink" data-id="element-629" data-mp="1ms8aokg9bvii">{event.title}</h2>
        <ul className="flex flex-col gap-1 text-small text-ink-muted sm:flex-row sm:flex-wrap sm:gap-x-5" data-id="element-630" data-mp="1muccmwulikg5">
          <li className="flex items-center gap-1.5" data-id="element-631" data-mp="18yqost03m6pb">
            <CalendarIcon aria-hidden="true" className="h-4 w-4" data-id="element-632" data-mp="1dywcxz75tb2n" />
            {event.date}
          </li>
          <li className="flex items-center gap-1.5" data-id="element-633" data-mp="z1vtsk31aofs">
            <ClockIcon aria-hidden="true" className="h-4 w-4" data-id="element-634" data-mp="141qluqyjpk2f" />
            {event.time}
          </li>
          <li className="flex items-center gap-1.5" data-id="element-635" data-mp="q28r7uykjcl5">
            <MapPinIcon aria-hidden="true" className="h-4 w-4" data-id="element-636" data-mp="q3jwjz7nwsed" />
            {event.place}
          </li>
        </ul>
      </div>

      <p className="text-body text-ink-muted" data-id="element-637" data-mp="v2wrl78ot5s1">{event.blurb}</p>
      <p className="text-small text-ink-muted" data-id="element-638" data-mp="2khb9p8p79uek">Organised by {event.host}.</p>

      <div className="flex flex-col gap-3 border-t border-rule pt-4" data-id="element-639" data-mp="2pfcsdsiprrpi">
        <div role="group" aria-label={`Are you going to ${event.title}?`} className="flex flex-wrap gap-2" data-id="element-640" data-mp="pclfipem2cpo">
          {CHOICES.map(choice => <button key={choice.value} type="button" aria-pressed={rsvp === choice.value} onClick={() => setRsvp(rsvp === choice.value ? null : choice.value)} className={`inline-flex min-h-tap items-center rounded-pill border px-4 text-small transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${rsvp === choice.value ? 'border-primary bg-primary font-medium text-on-primary' : 'border-rule text-ink hover:bg-canvas'}`} data-id="element-641" data-mp="kbxnc0enbptv">
              {choice.label}
            </button>)}
        </div>

        <p aria-live="polite" className="text-small text-ink-muted" data-id="element-642" data-mp="3eu1ajp0xnghp">
          {going.length} going
          {event.maybe.length > 0 ? ` · ${event.maybe.length} maybe` : ''}
          {fromYourYears > 0 ? ` · ${fromYourYears} ${fromYourYears === 1 ? 'person was' : 'people were'} here when you were` : ''}
          {rsvp === 'going' ? ' · your name is shown to other members' : ''}
        </p>

        <button type="button" onClick={() => setShowList(!showList)} aria-expanded={showList} className="inline-flex min-h-tap w-fit items-center rounded-control text-small text-primary-text underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-643" data-mp="28brn6xgui7om">
          {showList ? 'Hide who’s going' : 'See who’s going'}
        </button>

        {showList ? <ul className="flex flex-col divide-y divide-rule" data-id="element-644" data-mp="rxt6lzximg4b">
            {going.map(name => <AttendeeRow key={name} name={name} onOpenProfile={onOpenProfile} data-id="element-645" data-mp="wvunqfbds5d5" />)}
          </ul> : null}
      </div>
    </article>;
}
export function Events({
  onOpenProfile
}: EventsProps) {
  return <div className="min-h-full bg-card" data-id="element-646" data-mp="1v7e266wp6eg1">
      <div className="border-b border-rule bg-canvas" data-id="element-647" data-mp="1v6cp5ww4fhvo">
        <div className="mx-auto flex w-full max-w-4xl flex-col gap-2 px-4 py-6 sm:px-6" data-id="element-648" data-mp="28yagcspvmt3k">
          <h1 className="text-section-heading font-semibold text-ink" data-id="element-649" data-mp="24z1nrr6kp19l">Events</h1>
          <p className="max-w-[56ch] text-small text-ink-muted" data-id="element-650" data-mp="1h7w759st64y8">
            Gatherings members have organized. RSVPs are visible to other members, so people can
            see who else is coming. Nothing is shown outside the portal.
          </p>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 px-4 pb-16 pt-6 sm:px-6" data-id="element-651" data-mp="1q6q3r0652vbs">
        {EVENTS.map(event => <EventCard key={event.id} event={event} onOpenProfile={onOpenProfile} data-id="element-652" data-mp="31nq0q93flyc5" />)}

        <section aria-labelledby="past" className="mt-6 border-t border-rule pt-6" data-id="element-653" data-mp="2wpo8axk48mo0">
          <h2 id="past" className="text-heading font-semibold text-ink" data-id="element-654" data-mp="3566u8q8k8tvy">
            Already happened
          </h2>
          <ul className="mt-3 flex flex-col divide-y divide-rule" data-id="element-655" data-mp="398a6vi6yu4it">
            {PAST_EVENTS.map(event => <li key={event.title} className="flex flex-col gap-0.5 py-3" data-id="element-656" data-mp="2v8sjn79bizbk">
                <span className="text-body font-medium text-ink" data-id="element-657" data-mp="306u3huekj2o3">{event.title}</span>
                <span className="text-small text-ink-muted" data-id="element-658" data-mp="3nwogqd2sut8v">
                  {event.date} · {event.note}
                </span>
              </li>)}
          </ul>
          <p className="mt-4 max-w-[56ch] text-small text-ink-muted" data-id="element-659" data-mp="3sxubegg0sql1">
            Photographs from past events end up in the archive, filed under the year they happened.
          </p>
        </section>
      </div>
    </div>;
}